import { X } from "lucide-react";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Slider } from "../ui/slider";
import { useFilters } from "../../contexts/FilterContext";
import { CategorySelect } from "../CategorySelect";
import { LocationPicker } from "../LocationPicker";
import { useAuth } from "../../contexts/AuthContext";
import { profileApi } from "../../api/profileApi";
import { useState } from "react";
import { toast } from "react-hot-toast";

export const FiltersPanel = () => {
  const {
    filters,
    updateFilter,
    clearAllFilters,
    setLocationFilter,
    clearLocationFilter,
    setRadius,
  } = useFilters();
  const { currentUser, isAuthenticated, refreshUser } = useAuth();
  const [usingProfileLoc, setUsingProfileLoc] = useState(false);

  const handleCategoryChange = (category) => {
    updateFilter("category", category._id, {
      categoryName: category.name,
    });
  };

  /**
   * When the user picks a location in the picker, we apply it to the filter only
   * (we don't persist it to their profile — that's the settings page's job).
   *
   * Special case: if precision is "gps" (browser geolocation) the picker gives us
   * coords with no label. We round-trip through the backend to reverse-geocode it
   * so we have a proper label to display in active filters.
   */
  const handleLocationChange = async (locationData) => {
    if (locationData === null) {
      clearLocationFilter();
      return;
    }

    if (locationData.precision === "gps" && !locationData.label) {
      // Use the user's saved location-update endpoint to get a label.
      // This *does* persist to their profile too — which is arguably the right
      // behavior when they explicitly clicked "use my current location".
      // If we want it to only be a filter (no persistence), we'd need a separate
      // backend endpoint that just reverse-geocodes without saving. Keeping it
      // simple for now: GPS pick = save to profile too.
      if (!isAuthenticated) {
        toast.error("Log in to use your current location.");
        return;
      }

      try {
        const res = await profileApi.setLocationFromCoords(
          locationData.coordinates,
          "gps"
        );
        if (res.success && res.user?.location) {
          setLocationFilter(
            res.user.location.coordinates,
            res.user.location.label
          );
          await refreshUser();
        } else {
          toast.error(res.message || "Could not save location");
        }
      } catch (err) {
        toast.error("Could not save location");
      }
      return;
    }

    // Normal case: picked an autocomplete suggestion. We have everything we need.
    setLocationFilter(locationData.coordinates, locationData.label);
  };

  /**
   * Use the user's saved profile location as the filter center.
   * One-click shortcut for logged-in users with a saved location.
   */
  const handleUseProfileLocation = () => {
    if (!isAuthenticated) {
      toast.error("Log in to use your saved location.");
      return;
    }
    if (!currentUser?.location?.coordinates) {
      toast.error("Add a location to your profile in Settings first.");
      return;
    }
    setLocationFilter(
      currentUser.location.coordinates,
      currentUser.location.label || "Your saved location"
    );
    setUsingProfileLoc(true);
  };

  /**
   * Picker value — what we want the picker to display as the "currently selected
   * location" badge. Pulled from filter state (not user profile) since the user
   * may have picked a different filter location than their profile location.
   */
  const pickerValue =
    filters.useLocationFilter && filters.userCoordinates
      ? {
          coordinates: filters.userCoordinates,
          label: filters.userLocationLabel,
        }
      : null;

  const radiusKm = Math.round(filters.radius / 1000);

  return (
    <div className="mt-4 p-6 border border-border rounded-lg bg-white animate-fade-in">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-medium">Filters</h3>
        <Button
          variant="ghost"
          size="sm"
          className="text-sm h-8"
          onClick={clearAllFilters}
        >
          <X className="h-3.5 w-3.5 mr-1" />
          Clear All
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* Category */}
          <div>
            <Label htmlFor="category" className="block mb-2">
              Category
            </Label>
            <CategorySelect
              value={filters.category}
              onChange={handleCategoryChange}
            />
          </div>
        </div>

        <div className="space-y-6">
          {/* Location */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="block">Location</Label>
              {isAuthenticated && currentUser?.location?.coordinates && (
                <button
                  type="button"
                  onClick={handleUseProfileLocation}
                  className="text-xs text-igbanwo-mint hover:underline"
                >
                  Use saved location
                </button>
              )}
            </div>
            <LocationPicker
              value={pickerValue}
              onChange={handleLocationChange}
              countryCode="ZA"
              compact
            />
          </div>

          {/* Radius — only visible when a location filter is active */}
          {filters.useLocationFilter && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="block">Radius</Label>
                <span className="text-sm text-muted-foreground">
                  {radiusKm} km
                </span>
              </div>
              <Slider
                value={[filters.radius]}
                onValueChange={(values) => setRadius(values[0])}
                min={1000}
                max={100000}
                step={1000}
                className="data-[state=active]:bg-igbanwo-mint"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-1">
                <span>1 km</span>
                <span>100 km</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};